<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { toast } from "vue-sonner";
import { coercePastedLink } from "~/utils/media-url-platforms";

// Small toggle for single-URL inputs: shows "Paste" (clipboard) while the
// input is empty and "Clear" (X) once it has content.
//
// With `auto-submit`, a successful paste also submits the surrounding <form>
// when its submit button is enabled (the page's own validity rule decides).
// Opt-in on purpose: only free lookups use it (Download Subtitles, Region
// Checker, YouTube AI Tools, and the dashboard/landing link boxes, which only
// navigate). Never set it on inputs that start a
// credit-charging job such as Video to Subtitle or Video to Summary.
const props = withDefaults(
  defineProps<{ modelValue: string; disabled?: boolean; autoSubmit?: boolean }>(),
  { autoSubmit: false }
);
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "pasted"): void;
  (e: "cleared"): void;
}>();

const { t } = useI18n();
const hasContent = computed(() => props.modelValue.length > 0);
const buttonRef = ref<{ $el?: HTMLElement } | null>(null);

const submitSurroundingForm = async () => {
  // Let the parent re-render with the new value so its submit button's
  // disabled state reflects the pasted link.
  await nextTick();
  const form = buttonRef.value?.$el?.closest?.("form");
  if (!form) return;
  const submitter = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!submitter || submitter.disabled) return;
  if (typeof form.requestSubmit === "function") {
    form.requestSubmit(submitter);
  } else {
    submitter.click();
  }
};

// Checked after mount only: keeps SSR markup stable and hides the button on
// browsers without clipboard read support (old Firefox, insecure contexts).
const canPaste = ref(false);
onMounted(() => {
  canPaste.value = Boolean(navigator.clipboard?.readText);
});

const pasteFromClipboard = async () => {
  let text = "";
  try {
    text = await navigator.clipboard.readText();
  } catch {
    toast.error(t("errors.clipboardReadFailed"));
    return;
  }
  const link = coercePastedLink(text);
  if (!link) {
    toast.error(t("errors.clipboardNoLink"));
    return;
  }
  emit("update:modelValue", link);
  emit("pasted");
  if (props.autoSubmit) {
    await submitSurroundingForm();
  }
};

const clearInput = () => {
  emit("update:modelValue", "");
  emit("cleared");
};
</script>

<template>
  <Button
    v-if="hasContent || canPaste"
    ref="buttonRef"
    type="button"
    variant="ghost"
    size="icon"
    class="h-9 w-9 flex-none rounded-md text-muted-foreground [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
    :disabled="disabled"
    :aria-label="hasContent ? t('buttons.clear') : t('buttons.paste')"
    :title="hasContent ? t('buttons.clear') : t('buttons.paste')"
    @click="hasContent ? clearInput() : pasteFromClipboard()"
  >
    <Icon :name="hasContent ? 'lucide:x' : 'lucide:clipboard'" class="h-[18px] w-[18px]" />
  </Button>
</template>
